import React, { useState } from "react";
import { motion } from "framer-motion";
import { Song } from "../../types/song";
import { LISTEN_URL, ARTIST_NAME } from "../../config/site";
import { WhatsAppIcon, FacebookIcon, XIcon, CopyLinkIcon } from "./PlatformIcons";

interface ShareSongProps {
  song: Song;
}

function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  // Fallback for non-HTTPS / older browsers where the Clipboard API is unavailable
  return new Promise((resolve, reject) => {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      resolve();
    } catch (err) {
      reject(err);
    }
  });
}

const ShareSong: React.FC<ShareSongProps> = ({ song }) => {
  const [copied, setCopied] = useState(false);
  const shareText = `${song.title} by ${ARTIST_NAME}`;

  const shareLinks = [
    {
      id: "whatsapp",
      label: "WhatsApp",
      icon: WhatsAppIcon,
      href: `https://wa.me/?text=${encodeURIComponent(`${shareText} — ${LISTEN_URL}`)}`,
    },
    {
      id: "facebook",
      label: "Facebook",
      icon: FacebookIcon,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(LISTEN_URL)}`,
    },
    {
      id: "x",
      label: "X",
      icon: XIcon,
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(LISTEN_URL)}`,
    },
  ];

  const handleCopy = async () => {
    try {
      await copyToClipboard(LISTEN_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable — silently no-op, the link is visible on-page
    }
  };

  return (
    <section className="py-10 px-6 bg-white">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="max-w-3xl mx-auto text-center"
      >
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Share This Song</h2>
        <div className="flex flex-wrap justify-center gap-4">
          {shareLinks.map(({ id, label, icon: Icon, href }) => (
            <motion.a
              key={id}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="min-h-[56px] flex items-center gap-2 px-6 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold transition-colors"
            >
              <Icon className="w-5 h-5" />
              {label}
            </motion.a>
          ))}
          <motion.button
            type="button"
            onClick={handleCopy}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="min-h-[56px] flex items-center gap-2 px-6 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold transition-colors cursor-pointer"
          >
            <CopyLinkIcon className="w-5 h-5" />
            {copied ? "Copied!" : "Copy Link"}
          </motion.button>
        </div>
      </motion.div>
    </section>
  );
};

export default ShareSong;
